import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Commonwealth FC</title>
        <meta name="description" content="Commonwealth FC privacy policy detailing data collection, usage, and protection practices." />
      </Helmet>
      <div className="min-h-screen bg-[#0C2E3C]">
        {/* Header */}
        <div className="bg-[#0C2E3C] border-b border-[#F0F4F3]/10">
          <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
            <Link to="/" className="text-[#C8E64E] font-heading text-2xl tracking-wider hover:text-[#C8E64E]/80 transition-colors" data-testid="privacy-home-link">
              COMMONWEALTH FC
            </Link>
            <Link to="/terms" className="text-sm text-[#F0F4F3]/50 hover:text-[#C8E64E] transition-colors">
              Terms & Conditions
            </Link>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-6 py-12" data-testid="privacy-page">
          <h1 className="font-heading text-4xl text-[#C8E64E] mb-2">Privacy Policy</h1>
          <p className="text-[#F0F4F3]/40 text-sm mb-10">Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>

          <div className="prose-custom space-y-8">
            <section>
              <h2>Overview</h2>
              <p>Commonwealth FC ({"\""} we,{"\""} {"\""} us,{"\""} or {"\""} our{"\""}) is committed to protecting the privacy of our players, their families, and all visitors to our website. This Privacy Policy explains what information we collect, how we use it, and your rights regarding your data.</p>
            </section>

            <section>
              <h2>Information We Collect</h2>
              <p>When you interact with our website and services, we may collect the following information:</p>
              <div className="info-card">
                <p><strong>Registration & Interest Forms:</strong></p>
                <ul>
                  <li>Player name</li>
                  <li>Parent or guardian name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Age group</li>
                  <li>Soccer experience level</li>
                </ul>
                <p><strong>Coach/Team Inquiry Forms:</strong></p>
                <ul>
                  <li>Coach name and contact information</li>
                  <li>Organization name</li>
                  <li>Number of players</li>
                  <li>Inquiry message</li>
                </ul>
              </div>
              <p>We do not collect payment or financial information through our website. We do not collect information from children under 13 without parental consent — all registrations must be submitted by a parent or legal guardian.</p>
            </section>

            <section>
              <h2>How We Use Your Information</h2>
              <p>The information we collect is used solely for the following purposes:</p>
              <ul>
                <li>Processing your registration or inquiry</li>
                <li>Communicating tryout schedules, roster offers, and club announcements</li>
                <li>Sending follow-up messages related to your registration status</li>
                <li>Sending SMS notifications you have opted in to receive</li>
                <li>Responding to your questions and providing customer support</li>
                <li>Managing and improving our club operations</li>
              </ul>
            </section>

            <section>
              <h2>Information Sharing</h2>
              <div className="info-card highlight">
                <p><strong>We do not sell, rent, trade, or share your personal information with third parties for marketing purposes.</strong></p>
                <p>Your data is never shared with external organizations for their advertising, promotional, or commercial use.</p>
              </div>
              <p>We may share limited information only in the following circumstances:</p>
              <ul>
                <li><strong>Service providers:</strong> We use trusted service providers (such as email and SMS delivery platforms) to send communications on our behalf. These providers are contractually obligated to use your data only for the purpose of delivering our messages and are prohibited from using it for their own purposes.</li>
                <li><strong>Legal requirements:</strong> We may disclose information if required to do so by law or in response to valid legal requests from public authorities.</li>
                <li><strong>Safety:</strong> We may share information to protect the safety of our players, staff, or the public.</li>
              </ul>
            </section>

            <section>
              <h2>Data Storage & Security</h2>
              <p>Your personal information is stored securely using industry-standard encryption and access controls. We limit access to personal data to authorized club administrators only. We retain your information for as long as necessary to fulfill the purposes described in this policy, or as required by law.</p>
            </section>

            <section>
              <h2>SMS & Email Communications</h2>
              <p>By providing your phone number and opting in, you consent to receive SMS messages from Commonwealth FC. By providing your email address, you consent to receive email communications.</p>
              <div className="info-card">
                <p><strong>To stop SMS messages:</strong> Text <span className="keyword">STOP</span> at any time</p>
                <p><strong>For SMS help:</strong> Text <span className="keyword">HELP</span> at any time</p>
                <p><strong>To unsubscribe from emails:</strong> Click the unsubscribe link at the bottom of any email</p>
              </div>
              <p>Opting out of communications will not affect your registration status with the club.</p>
            </section>

            <section>
              <h2>Cookies & Analytics</h2>
              <p>Our website may use essential cookies to maintain functionality. We do not use third-party advertising trackers or sell browsing data. Any analytics data collected is used solely to improve website performance and user experience.</p>
            </section>

            <section>
              <h2>Your Rights</h2>
              <p>You have the right to:</p>
              <ul>
                <li><strong>Access</strong> the personal information we hold about you</li>
                <li><strong>Correct</strong> any inaccurate or incomplete information</li>
                <li><strong>Delete</strong> your personal data from our records</li>
                <li><strong>Opt out</strong> of communications at any time</li>
              </ul>
              <p>To exercise any of these rights, please contact us using the information below.</p>
            </section>

            <section>
              <h2>Children{"'"}s Privacy</h2>
              <p>Our services involve youth soccer programs. We do not knowingly collect personal information directly from children under 13. All registration forms are designed to be completed by a parent or legal guardian. If you believe we have inadvertently collected information from a child without parental consent, please contact us immediately and we will delete it.</p>
            </section>

            <section>
              <h2>Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated {"\""} Last updated{"\""} date. We encourage you to review this policy periodically.</p>
            </section>

            <section>
              <h2>Contact Us</h2>
              <div className="info-card">
                <p><strong>Commonwealth FC</strong></p>
                <p><strong>Email:</strong> info@commonwealthfc.com</p>
                <p><strong>Phone:</strong> (434) 722-3226</p>
                <p><strong>Website:</strong> <Link to="/" className="text-[#1FE3A8] hover:underline">commonwealthfc.com</Link></p>
              </div>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#F0F4F3]/10 py-6 text-center text-[#F0F4F3]/30 text-sm">
          <div className="max-w-3xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
            <span>&copy; {new Date().getFullYear()} Commonwealth FC</span>
            <Link to="/terms" className="text-[#F0F4F3]/40 hover:text-[#C8E64E] transition-colors">Terms & Conditions</Link>
          </div>
        </div>
      </div>

      <style>{`
        .prose-custom h2 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #F0F4F3;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid rgba(240, 244, 243, 0.08);
        }
        .prose-custom p {
          color: rgba(240, 244, 243, 0.65);
          line-height: 1.75;
          margin-bottom: 0.5rem;
        }
        .prose-custom ul {
          color: rgba(240, 244, 243, 0.65);
          padding-left: 1.5rem;
          margin: 0.75rem 0;
        }
        .prose-custom li {
          margin-bottom: 0.35rem;
          list-style-type: disc;
        }
        .prose-custom strong {
          color: #F0F4F3;
        }
        .prose-custom .info-card {
          background: rgba(240, 244, 243, 0.04);
          border: 1px solid rgba(240, 244, 243, 0.08);
          border-radius: 4px;
          padding: 1.25rem;
        }
        .prose-custom .info-card.highlight {
          border-color: rgba(31, 227, 168, 0.2);
          background: rgba(31, 227, 168, 0.05);
        }
        .prose-custom .info-card p {
          margin-bottom: 0.4rem;
        }
        .prose-custom .keyword {
          display: inline-block;
          background: rgba(200, 230, 78, 0.15);
          color: #C8E64E;
          font-weight: 700;
          padding: 0.1rem 0.5rem;
          border-radius: 3px;
          font-size: 0.95rem;
          letter-spacing: 0.05em;
        }
      `}</style>
    </>
  );
};

export default PrivacyPolicy;
